import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiDownload, FiFileText } from "react-icons/fi";
import { CAR_REPORT, CAR_REPORT_DETAIL } from "../../constant";
import { POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { scrollTop } from "../../utils/window";
import { conditionReportPdfHref } from "../Cars/printInspectionReport";
import InspectionReportBody, {
  unwrapReportList,
  overallFromSections,
} from "./InspectionReportBody";
import "./report.css";

function cleanValue(value) {
  if (value == null || value === "" || value === "None" || value === "N/A") {
    return null;
  }
  return String(value).trim() || null;
}

function getProductCodeFromLocation(location) {
  const params = new URLSearchParams(location?.search || "");
  return (
    location?.state?.product_code ||
    params.get("id") ||
    params.get("product_code") ||
    null
  );
}

function isCompletedReport(payload, sections, damagePoints) {
  if (unwrapReportList(sections).length > 0 || unwrapReportList(damagePoints).length > 0) {
    return true;
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  return Number(payload.total_inspect) > 0;
}

function getVehicleName(sections) {
  for (const section of sections || []) {
    for (const item of section?.inspection_title || []) {
      const selected = item?.selected_inspection_question?.[0];
      const name = cleanValue(selected?.car);
      if (name) return name;
    }
  }
  return "Vehicle inspection report";
}

function CarReport({
  car_report_data,
  car_report_detail_data,
  loading,
  detailLoading,
  car_report_call,
  car_report_detail_call,
}) {
  const location = useLocation();
  const history = useHistory();
  const productCode = getProductCodeFromLocation(location);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (!productCode) return;
    car_report_call({ id: productCode });
    car_report_detail_call({ id: productCode });
  }, [productCode]);

  const reportPayload = useMemo(() => {
    if (!car_report_detail_data || typeof car_report_detail_data !== "object") {
      return null;
    }
    return car_report_detail_data;
  }, [car_report_detail_data]);

  const sections = useMemo(
    () => unwrapReportList(car_report_detail_data),
    [car_report_detail_data]
  );
  const damagePoints = useMemo(
    () => unwrapReportList(car_report_data),
    [car_report_data]
  );
  const completed = isCompletedReport(reportPayload, sections, damagePoints);
  const calculated = overallFromSections(sections, damagePoints);
  const score = calculated.parts.length
    ? calculated.score
    : Math.round(Number(reportPayload?.total_inspect) || 0);
  const vehicleName = getVehicleName(sections);
  const busy = Boolean(loading || detailLoading);

  const pdfHref = completed ? conditionReportPdfHref(productCode) : "";

  if (!productCode) {
    return (
      <div className="pi-report">
        <div className="pi-report__shell">
          <div className="pi-report__empty">
            <FiFileText size={28} aria-hidden />
            <h1>No vehicle selected</h1>
            <p>
              Open a car from the listing and choose Report after inspection is
              complete.
            </p>
            <Link to="/cars" className="pi-report__btn">
              Browse used cars
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (busy && !completed) {
    return (
      <div className="pi-report">
        <div className="pi-report__shell">
          <div className="pi-report__loading" aria-live="polite">
            <span className="pi-report__spinner" aria-hidden />
            Loading inspection report…
          </div>
        </div>
      </div>
    );
  }

  if (!completed) {
    return (
      <div className="pi-report">
        <div className="pi-report__shell">
          <button
            type="button"
            className="pi-report__back"
            onClick={() => history.push("/cars")}
          >
            <FiArrowLeft size={16} aria-hidden />
            Back to cars
          </button>
          <div className="pi-report__empty">
            <FiFileText size={28} aria-hidden />
            <h1>Final report not ready</h1>
            <p>
              Reports are generated after vehicle registration and a completed
              inspection. This car does not have a final inspection report yet.
            </p>
            <ol className="pi-report__steps">
              <li>Vehicle registration</li>
              <li>Inspection in progress</li>
              <li>Final report published</li>
            </ol>
            <Link to="/cars" className="pi-report__btn">
              Back to used cars
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pi-report">
      <div className="pi-report__shell">
        <div className="pi-report__topbar no-print">
          <button
            type="button"
            className="pi-report__back"
            onClick={() => history.push("/cars")}
          >
            <FiArrowLeft size={16} aria-hidden />
            Back to cars
          </button>
          {pdfHref ? (
            <a
              className="pi-report-modal__download"
              href={pdfHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiDownload size={16} aria-hidden />
              Download PDF
            </a>
          ) : null}
        </div>

        <header className="pi-report__hero">
          <div className="pi-report__hero-copy">
            <p className="pi-report__eyebrow">Inspection report</p>
            <h1>{vehicleName}</h1>
            <p className="pi-report__lead">
              Final condition report generated after registration and completed
              inspection.
            </p>
            <div className="pi-report__flow" aria-label="Report process">
              <span>
                <FiCheckCircle size={14} aria-hidden /> Registered
              </span>
              <span>
                <FiCheckCircle size={14} aria-hidden /> Inspected
              </span>
              <span className="is-active">
                <FiCheckCircle size={14} aria-hidden /> Final report
              </span>
            </div>
          </div>
          <div
            className="pi-report__score"
            aria-label={`Overall inspection score ${score} out of 100`}
          >
            <strong>{score}</strong>
            <span>/100</span>
            <em>Overall score</em>
          </div>
        </header>

        <InspectionReportBody
          resetKey={productCode}
          sections={sections}
          damagePoints={damagePoints}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  car_report_data: state.carreport.car_report_data,
  car_report_detail_data: state.carreport.car_report_detail_data,
  loading: Boolean(state.carreport.loading),
  detailLoading: Boolean(state.carreport.winerListloading),
});

const mapDispatchToProps = (dispatch) => ({
  car_report_call: (data) =>
    dispatch({
      type: CAR_REPORT,
      payload: {
        type: CAR_REPORT,
        method: POST,
        label: Labels.car_report,
        hideOverlayLoader: true,
        data,
      },
    }),
  car_report_detail_call: (data) =>
    dispatch({
      type: CAR_REPORT_DETAIL,
      payload: {
        type: CAR_REPORT_DETAIL,
        method: POST,
        label: Labels.car_report_detail,
        hideOverlayLoader: true,
        data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CarReport);
